import type { AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import {
    getKpiBinLabelAffixesFromColumnOption,
    buildAxisGroupByControlsMeta,
    buildKpiAxisControlsMeta,
} from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import type { PpaMetaControlNode, PpaMetaOptionItem } from 'components/pages/PackArchitecture/packArchitectureMeta.types';

type WhitespaceMetaOptionItem = PpaMetaOptionItem;

export type WhitespaceAxisDropdownConfig = {
    id?: string;
    group_by_options?: WhitespaceMetaOptionItem[];
    column_options?: WhitespaceMetaOptionItem[];
    options?: PpaMetaControlNode[];
};

const toPpaAxisNode = (dropdown?: WhitespaceAxisDropdownConfig | null): PpaMetaControlNode | undefined => {
    if (!dropdown) return undefined;

    const axisOptions = dropdown.options ?? null;
    // X-axis config still ships its sort_by/show_top_n controls flat at the top
    // level (not yet wrapped in a `group_by_options` section like y_axis is),
    // so synthesize that wrapper here for the generic PPA meta helpers to find.
    const hasFlatAxisControls =
        axisOptions?.some((option) => option.id === 'sort_by' || option.id === 'range') ?? false;

    if (dropdown.id === 'x_axis' && hasFlatAxisControls) {
        return {
            id: dropdown.id,
            group_by_options: dropdown.group_by_options ?? null,
            column_options: dropdown.column_options ?? null,
            options: [
                {
                    id: 'group_by_options',
                    options: axisOptions,
                },
            ],
        };
    }

    // y_axis ships all selectable items under a single `column_options` array
    // (each tagged with `id_type`: `attribute_type` or `kpi_id`), and its
    // `options` settings sections are keyed by those same `id_type` values
    // (see backend whitespace_analysis.json) rather than by `group_by_options`/
    // `column_options`. Pass through as-is; the section lookup is done by the
    // whitespace-specific builders below via an explicit sectionId.
    return {
        id: dropdown.id,
        group_by_options: dropdown.group_by_options ?? null,
        column_options: dropdown.column_options ?? null,
        options: axisOptions,
    };
};

/** Minimal shape needed to build grouped axis dropdown options - satisfied by both
 * `WhitespaceMetaOptionItem` and the context's `WhitespaceOptionItem`. */
export type WhitespaceGroupableOptionItem = {
    id?: string;
    label?: string;
    dimension_label?: string | null;
};

// Whitespace axis values are keyed by label (not id, unlike PPA's AxisConfigDropdown usage) - see
// xAxis/yAxis state in WhitespaceAnalysisContext - so the option's `value` must stay the label here.
const toWhitespaceAxisOption = (item: WhitespaceGroupableOptionItem) => {
    const label = item.label ?? item.id ?? '';
    return { label, value: label };
};

/** Groups options by `dimension_label` (e.g. "Product", "Geo", "Price", "Pack"), mirroring
 * PPA's `buildColumnOptionsGroups`. Falls back to a single ungrouped group when no items carry
 * a `dimension_label`, so the dropdown panel renders without group headers as before. */
const buildGroupedWhitespaceOptions = (
    items: WhitespaceGroupableOptionItem[],
    fallbackTitle: string,
): AxisOptionGroup[] => {
    if (!items.length) return [];

    const hasDimensionLabels = items.some((item) => item.dimension_label);
    if (!hasDimensionLabels) {
        return [{ title: fallbackTitle, options: items.map(toWhitespaceAxisOption) }];
    }

    const groupsByLabel = new Map<string, WhitespaceGroupableOptionItem[]>();
    for (const item of items) {
        const groupTitle = item.dimension_label ?? fallbackTitle;
        const existing = groupsByLabel.get(groupTitle) ?? [];
        existing.push(item);
        groupsByLabel.set(groupTitle, existing);
    }

    return Array.from(groupsByLabel.entries()).map(([title, groupItems]) => ({
        title,
        options: groupItems.map(toWhitespaceAxisOption),
    }));
};

export const buildWhitespaceXAxisOptionGroups = (items: WhitespaceGroupableOptionItem[]): AxisOptionGroup[] =>
    buildGroupedWhitespaceOptions(items, 'X-Axis');

export const buildWhitespaceYAxisOptionGroups = (items: WhitespaceGroupableOptionItem[]): AxisOptionGroup[] =>
    buildGroupedWhitespaceOptions(items, 'Y-Axis');

export const buildWhitespaceXAxisGroupByControlsMeta = (
    xAxisDropdown?: WhitespaceAxisDropdownConfig | null,
    dimensionLabel = '',
    selectedXAxisId = '',
): AxisGroupByControlsMeta | null => {
    const fromMeta = buildAxisGroupByControlsMeta(toPpaAxisNode(xAxisDropdown), dimensionLabel, selectedXAxisId);
    if (!fromMeta) return null;
    return {
        ...fromMeta,
        ownDimensionSort: null,
    };
};

export const buildWhitespaceYKpiControlsMeta = (
    yAxisDropdown?: WhitespaceAxisDropdownConfig | null,
    sectionId = 'kpi_id',
): KpiAxisControlsMeta | null => buildKpiAxisControlsMeta(toPpaAxisNode(yAxisDropdown), sectionId);

/** Y-axis attribute (e.g. Pack_Range) sort/group-by settings — mirrors the X-axis dimension controls meta. */
export const buildWhitespaceYAxisGroupByControlsMeta = (
    yAxisDropdown?: WhitespaceAxisDropdownConfig | null,
    dimensionLabel = '',
    selectedYAxisId = '',
    sectionId = 'attribute_type',
): AxisGroupByControlsMeta | null => {
    const fromMeta = buildAxisGroupByControlsMeta(
        toPpaAxisNode(yAxisDropdown),
        dimensionLabel,
        selectedYAxisId,
        sectionId,
    );
    if (!fromMeta) return null;
    return {
        ...fromMeta,
        ownDimensionSort: null,
    };
};

export const getWhitespaceYKpiBinLabelAffixes = (
    yAxisOption?: WhitespaceMetaOptionItem | null,
    currencyPrefix = '',
) => getKpiBinLabelAffixesFromColumnOption(yAxisOption ?? null, currencyPrefix);
